import { ExternalLink, Sparkles, Terminal } from 'lucide-react'
import ModuleLayout from '../components/ModuleLayout'
import StepByStep from '../components/StepByStep'
import CopyBlock from '../components/CopyBlock'
import SheetSubmission from '../components/SheetSubmission'
import { FIRST_ASK_TAB, FIRST_ASK_VALUE } from '../data/apDataset'
import { MODULE_19_CHECKS } from '../data/apChecks'

/**
 * Module 1 of the AI track (id 19). Deliberately tiny.
 *
 * No <Quiz>: Quiz and SheetSubmission both write quizResults.percentageScore
 * and both call completeModule, so a module with both has them overwrite each
 * other depending on finish order.
 */

const PROMPT = `Here is a Google Sheet:
<paste your sheet link here>

Please rename the first tab to "${FIRST_ASK_TAB}" and put the text
"${FIRST_ASK_VALUE}" in cell A1. Don't change anything else.`

function SectionWhatYoullDo() {
  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">One cell. That's the whole exercise.</h2>
      <p className="mb-4 text-slate-600">
        You're going to make an empty Google Sheet and ask Claude to write one thing into one cell.
        It is not a useful thing to do — you could type it yourself in three seconds — and that's
        deliberate.
      </p>
      <p className="mb-4 text-slate-600">
        What you're actually watching for is the shape of the loop: it reads a link you paste, works
        out what you want, <strong>asks your permission before it writes anything</strong>, and then
        changes a real file in your Drive. Once you've seen that happen on something trivial, the
        rest of this course is the same loop pointed at bigger work.
      </p>
      <div className="rounded-xl border-2 border-teal-200 bg-teal-50 p-5">
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-teal-600" />
          <div>
            <h3 className="font-semibold text-teal-900">If this is your first time in a terminal</h3>
            <p className="mt-1 text-sm text-teal-800">
              Do{' '}
              <a href="/module/17" className="font-semibold underline">Getting Set Up</a>{' '}
              first — it's in the{' '}
              <a href="/courses/claude-basics" className="underline">
                Getting Started with Claude
              </a>{' '}
              course. Come back when typing <code>wa</code> gets you a prompt.
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

function SectionDoIt() {
  const steps = [
    {
      title: 'Make a blank spreadsheet',
      content: (
        <div className="space-y-2">
          <p>
            Go to{' '}
            <a
              href="https://sheets.new"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-blue-600 hover:underline"
            >
              sheets.new <ExternalLink className="h-3 w-3" />
            </a>{' '}
            — that shortcut makes a new, empty Google Sheet in your own Drive.
          </p>
          <p>
            Name it something you'll find again, like{' '}
            <strong>AI training — SAMPLE AP register</strong>. You'll keep using this one sheet for
            the rest of the course.
          </p>
        </div>
      ),
      highlight: 'Use your Finder account. Nothing in this course touches real finance data.',
    },
    {
      title: 'Copy the link out of the address bar',
      content: (
        <p>
          The whole address, the one that looks like{' '}
          <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">
            https://docs.google.com/spreadsheets/d/1AbC…/edit
          </code>
          . You'll paste it into Claude, and into the box at the bottom of this page.
        </p>
      ),
    },
    {
      title: 'Ask',
      content: (
        <div className="space-y-3">
          <p>
            Open your terminal, type <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">wa</code>{' '}
            and press Enter. Then paste this in, with your own link in the first line.
          </p>
          <CopyBlock text={PROMPT} label="Copy prompt" />
        </div>
      ),
      highlight:
        "It will ask before it writes. Read what it says it's about to do — that prompt is the control point, and it is the only one you get.",
    },
  ]

  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">Do it</h2>
      <StepByStep steps={steps} />
    </section>
  )
}

function SectionWhatToNotice() {
  const points = [
    {
      h: 'You gave it a link, not a file',
      p: "It didn't need access to your whole Drive, and you didn't have to share the sheet with anyone. It acts as you, so it can reach exactly what you can reach — no more.",
    },
    {
      h: 'It asked before writing',
      p: 'Every change to a real thing goes through that prompt. Reading is free; writing is not. When you get to work that matters, this is the moment to actually read.',
    },
    {
      h: "You described the outcome, not the steps",
      p: 'You never said "click cell A1, type this, press Enter". You said what the sheet should look like afterwards. That is the shift the whole course is about — and it is why being precise about the outcome starts to matter enormously.',
    },
  ]

  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">Worth noticing</h2>
      <div className="space-y-3">
        {points.map(({ h, p }) => (
          <div key={h} className="rounded-lg border border-slate-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-slate-800">{h}</h3>
            <p className="mt-1 text-sm text-slate-600">{p}</p>
          </div>
        ))}
      </div>
      <p className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
        <strong className="text-slate-800">Next:</strong> you'll build a whole invoice register in
        this sheet — forty rows of it — without typing any data or pasting any in. You'll describe
        what you want and have Claude write the script that produces it.
      </p>
    </section>
  )
}

function SectionTroubleshooting() {
  const problems = [
    {
      p: "It says it can't access the spreadsheet",
      s: "Check you pasted the whole URL, and that you're signed in with your Finder account. It reads the sheet as you — if you can open it, so can it.",
    },
    {
      p: 'It renamed the wrong tab, or made a new one',
      s: `Tell it: "the tab should be called ${FIRST_ASK_TAB} — please fix that". You don't have to start over; just say what's wrong.`,
    },
    {
      p: 'The text went in the wrong cell',
      s: 'Say which cell you meant. A1 is the very top-left one.',
    },
    {
      p: 'Something else entirely',
      s: 'Screenshot the error, crop out anything that looks like a password or a token, and paste it into Gemini or Antigravity. Describing an error badly is the biggest time-waster there is; a picture skips it.',
    },
  ]

  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">If it goes wrong</h2>
      <dl className="space-y-3">
        {problems.map(({ p, s }) => (
          <div key={p} className="rounded-lg border border-slate-200 bg-white p-4">
            <dt className="flex items-start gap-2 text-sm font-semibold text-slate-800">
              <Terminal className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              {p}
            </dt>
            <dd className="mt-1.5 pl-6 text-sm text-slate-600">{s}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

export default function ModuleFirstAsk() {
  return (
    <ModuleLayout moduleId={19}>
      <div className="space-y-12">
        <SectionWhatYoullDo />
        <SectionDoIt />
        <SectionWhatToNotice />
        <SectionTroubleshooting />
        <SheetSubmission
          moduleId={19}
          checks={MODULE_19_CHECKS}
          heading="Check your sheet"
          intro="Paste your spreadsheet link and we'll read it and tell you whether it worked. Check as many times as you like."
        />
      </div>
    </ModuleLayout>
  )
}
